import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useUserRole } from "@/hooks/useUserRole";
import { useUserOrg } from "@/hooks/useUserOrg";
import { useAgentMode } from "@/hooks/useAgentMode";
import { useHasResolvedOnce } from "@/hooks/useHasResolvedOnce";
import { BrandedLoadingSpinner } from "@/components/brand";
import { resolveRouteDecision } from "./protectedRouteDecision";
interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: "admin" | "agent";
  skipOrgCheck?: boolean;
  /**
   * Organization membership roles that may access this route even without the
   * platform `requiredRole`. Used to admit org editors (whose platform role is
   * `agent`) to the content/profile editing surfaces only. The check is on the
   * caller's role in their active org workspace, so it grants nothing outside an
   * org they belong to; every other admin route omits this and stays
   * platform-admin only.
   */
  allowOrgRoles?: string[];
  /**
   * For the caller's own history on an agent route (their reviews, their
   * messages): a platform admin whose only office was detached keeps the admin
   * role but has no workspace, and may still read it. Waives only the setup
   * redirect; see `resolveRouteDecision`.
   */
  allowWithoutOrg?: boolean;
}

const ProtectedRoute = ({ children, requiredRole, skipOrgCheck, allowOrgRoles, allowWithoutOrg }: ProtectedRouteProps) => {
  const { user, isLoading: authLoading } = useAuth();
  const { role, isLoading: roleLoading } = useUserRole();
  const { org, orgRole, isLoading: orgLoading } = useUserOrg();
  const { isAgentMode } = useAgentMode();
  const location = useLocation();
  const isAgentPortalRoute = location.pathname.startsWith("/agent/");
  const isAgentPortalContext = isAgentMode || isAgentPortalRoute;
  // Show the full-page loader only before the session/role/org checks have EVER
  // settled. After that first resolution, a loading state means a background
  // revalidation (Supabase fires an auth event on every tab focus) — swapping in
  // the loader there would unmount the entire route subtree and remount every
  // child, which reads to users as a full page reload. Fall through instead and
  // decide on the last resolved values, which the hooks keep during a refresh.
  const isResolving = authLoading || (user && roleLoading) || (user && orgLoading);
  const hasResolvedOnce = useHasResolvedOnce(!isResolving);

  if (isResolving && !hasResolvedOnce) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <BrandedLoadingSpinner size="lg" />
      </div>
    );
  }

  const decision = resolveRouteDecision({
    hasUser: !!user,
    role,
    orgRole,
    hasOrg: !!org,
    requiredRole,
    allowOrgRoles,
    skipOrgCheck,
    allowWithoutOrg,
    // Org account_type users can reach /admin/setup even before role promotion.
    isOrgAccount: user?.user_metadata?.account_type === "organization",
    isAgentPortalContext,
    pathname: location.pathname,
  });

  if (decision.kind === "redirect") {
    return <Navigate to={decision.to} replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
